import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { CategoryPickerCategory, CategoryPickerComponent } from "@me-pos/category-picker";
import { ReactSelectBrandsInterface, WriteOffFilterInterface } from "../../../../../interfaces/interfaces";
import { CategoryInterface } from "../../../types";
import { ReactSelect } from "@services/ui-components";
import i18next from "i18next";

type ProductsFiltersType = {
    onApplyFilters: (filters: WriteOffFilterInterface) => void;
    productCategoriesList: CategoryInterface[];
    brandsList: ReactSelectBrandsInterface[];
};

const ProductsFilters: React.FC<ProductsFiltersType> = (props) => {
    const { t: translate, i18n } = useTranslation();
    const inputSearchRef = useRef<HTMLInputElement>(null);
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [searchValue, setSearchValue] = useState<string>("");
    const [searchCategoryName, setSearchCategoryName] = useState<string>("");
    const [selectedBrand, setSelectedBrand] = useState<ReactSelectBrandsInterface>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [writeOffFilters, setWriteOffFilters] = useState<WriteOffFilterInterface>({
        categoryName: null,
        categoryId: null,
        subCategoryId: null,
        brand: null,
    });

    const handleIconClick = () => {
        inputSearchRef.current?.focus();
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLInputElement;
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(target) &&
                target.id !== "productsList-categoryId-field"
            ) {
                setIsPickerOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const onInputSearchHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const normalizedSearchValue: string = event.target.value.trim().replace(/\s+/g, " ");
        setSearchValue(event.target.value);
        setWriteOffFilters((prevState: WriteOffFilterInterface): WriteOffFilterInterface => {
            return {
                ...prevState,
                categoryName: normalizedSearchValue,
            };
        });
    };

    const handleCategoryIdChange = (categoryId: number) => {
        setWriteOffFilters((prevState: WriteOffFilterInterface): WriteOffFilterInterface => {
            return {
                ...prevState,
                categoryId: categoryId,
                subCategoryId: null,
            };
        });
    };
    const handleSubCategoryIdChange = (categoryId: number, subCategoryId: number) => {
        setWriteOffFilters((prevState: WriteOffFilterInterface): WriteOffFilterInterface => {
            return {
                ...prevState,
                categoryId: categoryId,
                subCategoryId: subCategoryId,
            };
        });
    };

    const handleBrandChange = (currentBrand: ReactSelectBrandsInterface) => {
        setSelectedBrand(currentBrand);
        setWriteOffFilters((prevState) => {
            return {
                ...prevState,
                brand: currentBrand.value,
            };
        });
    };

    const clearFiltersValues: WriteOffFilterInterface = {
        categoryName: null,
        categoryId: null,
        subCategoryId: null,
        brand: null,
    };

    return (
        <Row className="mb-5">
            <Col lg={9} className={"mb-3 mb-lg-0"}>
                <Row>
                    <Col lg={4}>
                        <div className="position-relative mb-3 mb-lg-0">
                            <input
                                id="writeOff-produt-search-field"
                                type="text"
                                className="form-control pe-4"
                                value={searchValue}
                                placeholder={translate("Search by product name")}
                                ref={inputSearchRef}
                                onChange={onInputSearchHandler}
                            />
                            <i
                                className="bi bi-search position-absolute top-50 end-0 translate-middle-y text-dark me-2"
                                onClick={handleIconClick}
                                role="button"
                                tabIndex={0}
                            ></i>
                        </div>
                    </Col>
                    <Col lg={4}>
                        <div className="position-relative mb-3 mb-lg-0">
                            <input
                                type="text"
                                id="productsList-categoryId-field"
                                className="form-control px-4 cursor-pointer"
                                placeholder={translate("Category")}
                                value={searchCategoryName}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setIsPickerOpen((prev) => !prev);
                                }}
                                readOnly
                            />
                            <i
                                className={`bi bi-chevron-down position-absolute top-50 end-0 text-dark me-2 pointer-event-none transition-transform-icon ${isPickerOpen ? "rotate-180-icon" : ""}`}
                            ></i>
                            <div
                                ref={dropdownRef}
                                className="dropdown position-absolute opacity-1 z-2 top-10  start-0 end-0 mt-2 bg-white shadow"
                            >
                                {props.productCategoriesList && (
                                    <CategoryPickerComponent
                                        i18n={i18next}
                                        isOpen={isPickerOpen}
                                        onSearchChange={() => {}}
                                        onChangeCategory={(currentCategory: CategoryPickerCategory) => {
                                            if (currentCategory.isPicked) {
                                                setSearchCategoryName(currentCategory?.name);
                                                handleCategoryIdChange(currentCategory?.id);
                                                setIsPickerOpen(false);
                                            } else {
                                                setSearchValue("");
                                            }
                                        }}
                                        onChangeSubcategory={(currentSubcategory, currentCategory) => {
                                            if (currentSubcategory?.isPicked) {
                                                setSearchCategoryName(currentSubcategory?.name);
                                                handleSubCategoryIdChange(currentCategory?.id, currentSubcategory?.id);
                                                setIsPickerOpen(false);
                                            } else {
                                                setSearchValue("");
                                            }
                                        }}
                                        serviceCategories={props.productCategoriesList}
                                    />
                                )}
                            </div>
                        </div>
                    </Col>
                    <Col lg={4}>
                        <ReactSelect
                            id="products-list-brand-field"
                            value={selectedBrand}
                            options={props.brandsList}
                            placeholder={translate("Brand")}
                            onChange={handleBrandChange}
                            isSearchable={false}
                        />
                    </Col>
                </Row>
            </Col>
            <Col lg={3}>
                <Row className="gx-3 h-100">
                    <Col lg={6} className="mb-3 mb-lg-0">
                        <Button
                            type="button"
                            variant="outline-dark"
                            className="border-grey w-100 h-100 px-1 fw-normal"
                            onClick={() => {
                                setWriteOffFilters(clearFiltersValues);
                                setSearchValue("");
                                setSearchCategoryName("");
                                setSelectedBrand(null);
                                props.onApplyFilters(clearFiltersValues);
                            }}
                        >
                            {translate("clear")}
                        </Button>
                    </Col>
                    <Col lg={6} className="mb-3 mb-lg-0">
                        <Button
                            variant="primary"
                            className="w-100 h-100 px-1 fw-normal"
                            onClick={() => props.onApplyFilters(writeOffFilters)}
                        >
                            {translate("apply")}
                        </Button>
                    </Col>
                </Row>
            </Col>
        </Row>
    );
};

export default React.memo(ProductsFilters);
